import Vue from 'vue'
import { createAuth0Client } from '@auth0/auth0-spa-js'
import store from '@/store'
import router from '@/router'
import _ from 'lodash'

import { AUTH0_CONFIG } from '@/config'

// Define a default action to perform after authentication
const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname)

// Auth0 client variables
let instance

// Idle variables
let idleTimer = 0, idleInterval
// Events for resetting the idle timer
const idleEventListeners = [
  'load',
  'click',
  'mousemove',
  'mousedown',
  'touchmove',
  'keydown',
  'wheel',
  'mousewheel',
  'DOMMouseScroll'
]

// Returns the current instance of the SDK
export const getInstance = () => instance

// Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance
export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  // The URL where Auth0 will redirect your browser to with the authentication result.
  // It must be whitelisted in the "Allowed Callback URLs" field in your Auth0 Application's settings.
  redirectUri = window.location.origin,
  ...options
}) => {
  if (instance) return instance

  // The 'instance' is simply a Vue object
  instance = new Vue({
    data () {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null,
        inactivityTimeout: 3600, // in seconds
        logoutOnInactivity: true
      }
    },
    methods: {
      // Authenticates the user using a popup window
      async loginWithPopup(o) {
        this.popupOpen = true
        try {
          await this.auth0Client.loginWithPopup(o)
        } catch (e) {
          // console.error(e)
          this.error = e
        } finally {
          this.popupOpen = false
        }
        this.user = await this.auth0Client.getUser()
        this.isAuthenticated = true
      },
      // Handles the callback when logging in using a redirect
      async handleRedirectCallback () {
        this.loading = true
        try {
          await this.auth0Client.handleRedirectCallback()
          this.user = await this.auth0Client.getUser()
          this.isAuthenticated = true
        } catch (e) {
          // console.error(e)
          this.error = e
        } finally {
          this.loading = false
        }
      },
      // Authenticates the user using the redirect method
      loginWithRedirect (o) {
        return this.auth0Client.loginWithRedirect(o)
      },
      // Returns all the claims present in the ID token
      getIdTokenClaims (o) {
        return this.auth0Client.getIdTokenClaims(o)
      },
      // Returns the access token. If the token is invalid or missing, a new one is retrieved
      getTokenSilently (o) {
        return this.auth0Client.getTokenSilently(o)
      },
      // Gets the access token using a popup window
      getTokenWithPopup (o) {
        return this.auth0Client.getTokenWithPopup(o)
      },
      async getAuthToken() {
        let token
        try {
          // Try getting access token.
          token = await this.getTokenSilently()
        } catch (e) {
          // If refresh token is missing or login is required, try logging in again.
          if (e.error === 'missing_refresh_token' || e.error === 'login_required') {
            this.loginWithRedirect({ appState: { targetUrl: router.currentRoute.fullPath } })
            return
          }
          // If another error occured, show error.
          store.dispatch('setNotification', { message: e.message, isAuthError: true })
          return
        }
        return token
      },
      // Logs the user out and removes their session on the authorization server
      logout (o) {
        const opts = {
          returnTo: window.location.origin,
          ...o
        }
        store.dispatch('resetUser')
        localStorage.clear()
        return this.auth0Client.logout(opts)
      },
      initIdleTimer () {
        _.each(idleEventListeners, event => {
          window.addEventListener(event, this.resetIdleTimer)
        })
      },
      destroyIdleTimer () {
        _.each(idleEventListeners, event => {
          window.removeEventListener(event, this.resetIdleTimer)
        })
      },
      resetIdleTimer () {
        // Clear the previous interval
        clearInterval(idleInterval)
        // Reset the timer
        idleTimer = 0
        // Set a new interval
        idleInterval = setInterval(() => {
          // Increment timer
          idleTimer = idleTimer + 1
          // console.log('Idle since ' + idleTimer + ' seconds')
          // The user was inactive for more than the timout allows.
          // Therefore, we log the user out.
          if (idleTimer >= this.inactivityTimeout && this.logoutOnInactivity) {
            this.logout()
          }
        }, 1000)
      }
    },
    // Use this lifecycle method to instantiate the SDK client
    async created () {
      store.dispatch('setLoading', true)

      // Create a new instance of the SDK client using members of the given options object
      this.auth0Client = await createAuth0Client({
        domain: options.domain || AUTH0_CONFIG.domain,
        clientId: options.clientId || AUTH0_CONFIG.clientId,
        authorizationParams: {
          audience: options.audience || AUTH0_CONFIG.audience,
          redirect_uri: redirectUri
        },
        cacheLocation: AUTH0_CONFIG.cacheLocation || 'localstorage',
        useRefreshTokens: true
      })

      try {
        // If the user is returning to the app after authentication.
        if (
          window.location.search.includes('state=') &&
          (window.location.search.includes('code=') || window.location.search.includes('error='))
        ) {
          // Handle the redirect and retrieve tokens
          const { appState } = await this.auth0Client.handleRedirectCallback()

          // Notify subscribers that the redirect callback has happened, passing the appState
          // (useful for retrieving any pre-authentication state)
          onRedirectCallback(appState)

          // If handling the callback was successful, the parameters should be removed from the URL
          // so the callback handler will not be triggered the next time the page loads.
          window.history.replaceState({}, document.title, '/')
        }
      } catch (e) {
        // console.error(e)
        this.error = e
        store.dispatch('setNotification', { message: e.message, isAuthError: true })
      } finally {
        // Initialize our internal authentication state
        this.isAuthenticated = await this.auth0Client.isAuthenticated()
        this.user = await this.auth0Client.getUser()

        if (this.isAuthenticated) {
          // Check if user data is in local storage
          await store.dispatch('refreshUser')
          // If user data is not present, check if user is registered in Launch Feedback database
          if (!store.state.user.client) await store.dispatch('getUser')
        }

        this.loading = false
        store.dispatch('setLoading', false)
      }
    }
  })

  return instance
}

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0 = {
  install (Vue, options) {
    Vue.prototype.$auth = useAuth0(options)
  }
}
